/**
 * The drain's headwall where it leaves the farm — beat 10's one landmark.
 *
 * `DITCH_AT` runs x −22 … +14 while `HEDGEROW_BOUNDS` stops at −17, so the
 * channel has always been modelled five metres past the perimeter: the water
 * visibly leaves the field and the only thing stopping her following it is an
 * invisible collider. This is the object that turns that into a place. Beat 10's
 * whole throughput limit — one animal at a time, about four seconds each — is a
 * property of a pipe, so the pipe has to READ as a pipe from across a field or
 * the beat is asking the player to take a rule on trust.
 *
 * **+Z is the APPROACH — the face she sees.** It faced −Z in the first build and
 * this paragraph still said so after the geometry was turned round; an
 * independent review found the contradiction between it and `MOUTH_Z` below,
 * which is exactly the class of confident wrong comment D-097 is about. Placed,
 * the asset takes a quarter turn so +Z is world +X, back up the channel toward
 * the farm, and its buried half disappears into `bound-west`.
 */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

/**
 * 0.70 m of bore, and it is a character constraint before it is a dimension.
 *
 * Rosalie is 0.32 m tall (`ROSALIE_SCALE.height`, D-012). The barrel sits on the
 * channel bed, so the opening's crown is `BARREL_RADIUS + CULVERT_BORE_RADIUS` =
 * **0.77 m above the bed and 0.45 m over her head**, through 0.18 m of standing
 * water. **An independent review caught the first version of this sentence
 * claiming "half a metre of clear air over her"**, which was the air above the
 * water rather than above the pig — a number checked against nothing. A standing
 * sheep does not fit, and that is the point: beat 9 has composed every
 * two-shot as a circle and a column, and this is where the column is the wrong
 * shape for the only way out. **Stage 6 must measure Holly's shoulder against
 * this number rather than trusting this sentence** — `holly.ts` has already
 * shipped one constant that described geometry and disagreed with the mesh by
 * 95 mm (D-155), and the rule that came out of it is in `docs/traps.md`: if a
 * constant describes geometry, measure the node.
 */
export const CULVERT_BORE_RADIUS = 0.35;
/** 70 mm of concrete, so the cut end at the mouth is a ring and not a line. */
const BARREL_RADIUS = 0.42;

/**
 * ── The silhouette, which is what the first build got wrong ──────────────────
 *
 * A headwall is a wall, and the first version was one: 2.2 × 1.1 × 0.28 with the
 * pipe buried in it. At 64 px it was **a black rectangle** — a skip, a bench, a
 * kerb — because a hole is not a silhouette feature and the barrel never broke
 * the outline. `docs/asset-conventions.md` §5 calls that gate the one that fails
 * most often and it duly failed.
 *
 * Two changes, and both of them are also what a real culvert looks like:
 *
 * 1. **A raised centre over the bore and lower shoulders either side**, so the
 *    top edge steps twice instead of running flat across two metres.
 * 2. **The barrel is proud on BOTH faces** — 0.40 m at the mouth and the same
 *    behind, where it disappears into the bank. That is the pipe continuing, and
 *    it is what puts a cylinder into the outline from every oblique angle.
 *
 * It also centres the mesh in Z, which the asset contract requires to 30 mm and
 * a one-sided snout does not.
 */
const CENTRE_WIDTH = 1.1;
const SHOULDER_WIDTH = 0.55;
export const CULVERT_WALL_WIDTH = CENTRE_WIDTH + SHOULDER_WIDTH * 2;
export const CULVERT_WALL_HEIGHT = 1.1;
const SHOULDER_HEIGHT = 0.62;
const WALL_THICKNESS = 0.28;
const COPING_HEIGHT = 0.09;
/** How far the barrel stands proud of each face. */
const SNOUT = 0.4;
const PIPE_LENGTH = WALL_THICKNESS + SNOUT * 2;

/**
 * The bore's centre height, and it is `BARREL_RADIUS` rather than the bore's.
 *
 * **The first version put the invert on the ground and the barrel 70 mm through
 * it**, which is what a pipe does: its outside is a wall thickness below the
 * water it carries. `asset.spec.ts`'s origin contract caught it on the first run
 * — *"floats or sinks (min.y): Expected > −0.001, Received −0.06999998688697828"*,
 * to the millimetre the wall is thick. Sitting the barrel on the bed instead is
 * also the honest reading: a laid pipe rests on the bottom of the channel and the
 * water pools in front of its lip.
 */
const BORE_Y = BARREL_RADIUS;
/** The mouth plane. **+Z is the approach side**, so this is the face she sees. */
const MOUTH_Z = PIPE_LENGTH / 2;

/**
 * The concrete's own extents, exported because the ROOM's collision derives from
 * them and must not restate them.
 *
 * `hedgerowSolids()` builds this as shoulders, jambs and a lintel around a bore
 * that deliberately has no collider at all, and every one of those boxes has to
 * meet the concrete exactly — including the shoulders being lower than the centre,
 * which is the difference between collision that matches the picture and an
 * invisible half-metre of wall at each end. Written as literals over there, every
 * one of them would be a measurement checked against nothing, which is the failure
 * `docs/traps.md` records against `holly.ts`'s 95 mm.
 */
export const CULVERT_DIMENSIONS = {
  boreRadius: CULVERT_BORE_RADIUS,
  barrelRadius: BARREL_RADIUS,
  centreHalfWidth: CENTRE_WIDTH / 2,
  shoulderWidth: SHOULDER_WIDTH,
  shoulderHeight: SHOULDER_HEIGHT,
  wallWidth: CULVERT_WALL_WIDTH,
  wallHeight: CULVERT_WALL_HEIGHT,
  halfThickness: WALL_THICKNESS / 2,
  /**
   * The lip on top of the centre block, exported for the same reason as the rest.
   *
   * It overhangs by 60 mm in width and 40 mm in depth and stands 90 mm proud, and
   * **the first version of the room's collision did not know it existed** — an
   * independent review found the gap. She is 0.32 m tall and cannot jump (D-017),
   * so nothing she can do reaches 1.10 m; it gets a collider anyway, because
   * *every visible object is in both lists* is a rule this room already keeps
   * without an exception in it, and it keeps it for a field tree nobody can
   * reach either.
   */
  copingHalfWidth: (CENTRE_WIDTH + 0.12) / 2,
  copingHalfThickness: (WALL_THICKNESS + 0.08) / 2,
  copingHeight: COPING_HEIGHT,
  /** Half the barrel's length: mouth to buried end, centred on the wall's plane. */
  halfBarrelLength: PIPE_LENGTH / 2,
  /** Height of the opening's floor above the asset's ground contact. See `BORE_Y`. */
  boreInvert: BARREL_RADIUS - CULVERT_BORE_RADIUS,
} as const;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'culvert-headwall';

  // Two kinds, not three. `mineral` is this project's concrete — the same hex
  // the service corridor's `drain-channel` uses, because a farm pours one kind of
  // concrete and a second grey here would read as two different objects.
  const concrete = surface('mineral', 0x69736f);

  const centre = new THREE.Mesh(
    new THREE.BoxGeometry(CENTRE_WIDTH, CULVERT_WALL_HEIGHT, WALL_THICKNESS),
    concrete,
  );
  centre.name = 'headwall-centre';
  centre.position.y = CULVERT_WALL_HEIGHT / 2;
  centre.castShadow = true;
  centre.receiveShadow = true;
  group.add(centre);

  const coping = new THREE.Mesh(
    new THREE.BoxGeometry(CENTRE_WIDTH + 0.12, COPING_HEIGHT, WALL_THICKNESS + 0.08),
    concrete,
  );
  coping.name = 'headwall-coping';
  coping.position.y = CULVERT_WALL_HEIGHT + COPING_HEIGHT / 2;
  coping.castShadow = true;
  group.add(coping);

  for (const side of [-1, 1] as const) {
    const shoulder = new THREE.Mesh(
      new THREE.BoxGeometry(SHOULDER_WIDTH, SHOULDER_HEIGHT, WALL_THICKNESS),
      concrete,
    );
    shoulder.name = `headwall-shoulder-${side < 0 ? 'left' : 'right'}`;
    shoulder.position.set(side * (CENTRE_WIDTH + SHOULDER_WIDTH) / 2, SHOULDER_HEIGHT / 2, 0);
    shoulder.castShadow = true;
    shoulder.receiveShadow = true;
    group.add(shoulder);
  }

  // The pipe is built as three flat pieces rather than as a tube with flipped
  // normals: the outside, the cut ring at the mouth, and a dark disc set 0.30 m
  // back. **A bore drawn as one black circle reads as paint**, and the toon
  // shader has no interior lighting to give a real tube — the ring is what
  // supplies the wall thickness the eye needs, and the recess is what supplies
  // the depth. Nothing in this repo sets `Material.side`, and this asset is not
  // the place to find out how the three-band ramp treats a backface.
  const pipe = new THREE.Mesh(
    new THREE.CylinderGeometry(BARREL_RADIUS, BARREL_RADIUS, PIPE_LENGTH, 16, 1, true),
    concrete,
  );
  pipe.name = 'culvert-pipe';
  pipe.rotation.x = Math.PI / 2;
  pipe.position.y = BORE_Y;
  pipe.castShadow = true;

  const rim = new THREE.Mesh(new THREE.RingGeometry(CULVERT_BORE_RADIUS, BARREL_RADIUS, 16), concrete);
  rim.name = 'culvert-rim';
  rim.position.set(0, BORE_Y, MOUTH_Z);

  const bore = new THREE.Mesh(new THREE.CircleGeometry(CULVERT_BORE_RADIUS, 16), surface('mineral', 0x232a27));
  bore.name = 'culvert-bore';
  bore.position.set(0, BORE_Y, MOUTH_Z - 0.3);

  group.add(pipe, rim, bore);

  // ── Two bent stubs where a grating used to be ────────────────────────────
  //
  // §2's content restraint, and the cheapest sentence in the beat: a drain that
  // leaves a farm is barred, this one is not, and nobody has to be told why the
  // way is open. Rust rather than galvanised — the bars are what is left, not
  // what is maintained.
  const steel = surface('metal', 0x7a5a44);
  for (const side of [-1, 1] as const) {
    const stub = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.16, 0.045), steel);
    stub.name = `grating-stub-${side < 0 ? 'left' : 'right'}`;
    stub.position.set(side * 0.22, BORE_Y + CULVERT_BORE_RADIUS - 0.02, MOUTH_Z - 0.03);
    stub.rotation.z = side * 0.35;
    stub.castShadow = true;
    group.add(stub);
  }

  return group;
}

export const culvertHeadwallAsset: AssetDefinition = {
  id: 'culvert-headwall',
  label: 'Culvert headwall',
  description: 'Concrete headwall and 0.70 m pipe where the farm drain leaves the field. The grating is gone.',
  footprint: { width: CULVERT_WALL_WIDTH, height: CULVERT_WALL_HEIGHT + COPING_HEIGHT, length: PIPE_LENGTH },
  create: build,
};
